import {
  ASC,
  checkboxFilter,
  CountBadge,
  Css,
  DESC,
  Direction,
  FilterDefs,
  GridSortConfig,
  multiFilter,
  NestedOption,
  Tokens,
  treeFilter,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useEffect, useLayoutEffect, useMemo } from "react";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import {
  CostType,
  ItivOrderField,
  LocationType,
  MaterialType,
  Order,
  TakeoffLineItemFilter,
  TakeoffLineItemOrder,
} from "src/generated/graphqlTypes";
import useZodQueryString from "src/hooks/useZodQueryString";
import {
  UseTakeoffsLayoutState_LocationFragment,
  UseTakeoffsLayoutState_OptionFragment,
  UseTakeoffsLayoutStateQuery,
  useUseTakeoffsLayoutStateQuery,
} from "src/routes/libraries/plan-package-v2/components/takeoffs/useTakeoffsLayoutState.generated";
import {
  useTakeoffsManagerContext,
  useTakeoffsStore,
} from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";
import { maybeSplitRoomNumber, searchSchema } from "src/utils";
import { JsonParam, useQueryParams } from "use-query-params";
import { z } from "zod";

/**
 * The takeoffs table's search, filters, group by and header sort, as the query they make.
 *
 * Built alongside Beam's `useGridTableLayoutState`, which owns the search, the persisted filters and the group by
 * that `GridTableLayout` renders. This adds what Beam's hook doesn't: the takeoffs filter defs and their facets, the
 * header sort (kept in the URL beside Beam's params), the mapping to the server's filter and order, and pruning.
 * Saves and copies still go through the takeoffs store, so the query is mirrored into it.
 */
export function useTakeoffsLayoutState() {
  const planPackageId = useTakeoffsStore((state) => state.planPackage.id);
  const versionId = useTakeoffsStore((state) => state.planPackage.version.id);
  const planName = useTakeoffsStore((state) => state.planPackage.name);
  const takeoffsStore = useTakeoffsManagerContext();
  const { data, refetch } = useUseTakeoffsLayoutStateQuery({ variables: { planPackageId, versionId } });
  const facets = useMemo(() => data && toFacets(data.planPackage), [data]);
  const filterDefs = useMemo(() => createFilterDefs(facets, planName), [facets, planName]);
  // Beam's half: `?filter=` (plus sessionStorage), `?search=` and `?groupBy=`, handed straight to `GridTableLayout`
  const layoutState = useGridTableLayoutState<TakeoffsFilter>({
    persistedFilter: { filterDefs, storageKey: `${planPackageId}_takeoffsFilter` },
    search: "server",
    groupBy: groupByOptions,
  });
  // Ours: Beam's hook has no sort state, so the header sort's `?sortBy=` and `?sortDirection=` live here
  // TODO: Beam gap (DS-412): swap for `useGridTableLayoutState`'s sort option once Beam is bumped past DS-412
  const [{ search: searchParam, sortBy: sortByParam, sortDirection: sortDirectionParam }, setQs] =
    useZodQueryString(querySchema);

  // Beam fills its search box from ?search= but only reports typing, so a deep link searches from the URL
  // TODO: Beam gap (DS-377): use just `layoutState.searchString` once Beam is bumped past DS-377, which applies
  // ?search= on load
  const search = layoutState.searchString || searchParam;
  const filter = useMemo(() => toTakeoffLineItemFilter(layoutState.filter, search), [layoutState.filter, search]);
  const groupByParam = layoutState.groupBy?.value;
  const groupBy = isGroupBy(groupByParam) ? groupByParam : ItivOrderField.CostCode;
  const sortBy = sortByFields.find((field) => field === sortByParam) ?? ItivOrderField.Item;
  const sortDirection = sortDirectionParam === Order.Desc ? Order.Desc : Order.Asc;
  // The group stays the first order, so a header sort only reorders lines within their group
  const order = useMemo<TakeoffLineItemOrder[]>(
    () => [
      { field: groupBy, direction: Order.Asc },
      { field: sortBy, direction: sortDirection },
    ],
    [groupBy, sortBy, sortDirection],
  );
  const sorting = useMemo<GridSortConfig>(
    () => ({
      on: "server",
      value: [sortBy, sortDirection === Order.Desc ? DESC : ASC],
      // Clearing the sort passes undefineds, which drop the params back to the default
      onSort: (field, direction) => setQs({ sortBy: field, sortDirection: direction && toOrder(direction) }),
    }),
    [sortBy, sortDirection, setQs],
  );

  // Writes Beam's `?filter=` directly: unlike Beam's `setFilter`, it's stable and takes an update type.
  // Beam's sessionStorage follows the URL, and these filters store their values as-is.
  const [, setFilterParam] = useQueryParams({ filter: JsonParam });

  // Saves and copies merge into the table's cache entry by the store's filter and order, then refetch the facets
  useLayoutEffect(() => {
    takeoffsStore.setState({
      filter,
      itemTableGroupBy: groupBy,
      itemTableSortBy: sortBy,
      itemTableSortDirection: sortDirection,
      refetchFilters: refetch,
      clearFilters: () => setFilterParam({ filter: {} }),
    });
  }, [takeoffsStore, filter, groupBy, sortBy, sortDirection, refetch, setFilterParam]);

  // A save, copy or new version can drop a facet, so drop the selections that went with it. Replace rather than push,
  // or Back lands on the stale filter, which prunes again and traps the user on the page.
  useEffect(() => {
    const pruned = facets && pruneFilter(layoutState.filter, facets);
    if (pruned) setFilterParam({ filter: pruned }, "replaceIn");
  }, [facets, layoutState.filter, setFilterParam]);

  return { layoutState, sorting, filter, order, groupBy };
}

/** What the toolbar filters on. Elevations, options and the base plan all narrow the server's `options`, OR'd. */
type TakeoffsFilter = {
  elevations?: string[];
  options?: string[];
  costCode?: string[];
  itemType?: ItemType[];
  locationInPath?: string[];
  basePlan?: boolean;
  modifiedInCurrentVersion?: boolean;
};

/** The toolbar's filter and search as the server's TLI filter. */
function toTakeoffLineItemFilter(filter: TakeoffsFilter, search: string): TakeoffLineItemFilter {
  const { elevations = [], options = [], basePlan, itemType = [], ...serverFields } = filter;
  // `null` matches lines with no options, i.e. the base plan's
  const optionIds = [...elevations, ...options, ...(basePlan ? [null] : [])];
  // As in v1, slots filter on material type and the rest on cost type, which the server ANDs
  const costType = itemType.flatMap((type) => (type === MaterialType.Placeholder ? [] : [type]));
  const materialType = itemType.flatMap((type) => (type === MaterialType.Placeholder ? [type] : []));
  return {
    ...serverFields,
    ...(optionIds.nonEmpty && { options: optionIds }),
    ...(costType.nonEmpty && { costType }),
    ...(materialType.nonEmpty && { materialType }),
    ...(search ? { search } : {}),
  };
}

type ItemType = MaterialType.Placeholder | CostType.Labor | CostType.Materials;

const itemTypeOptions: { id: ItemType; name: string }[] = [
  { id: MaterialType.Placeholder, name: "Item Slots" },
  { id: CostType.Labor, name: "Labor (Tasks)" },
  { id: CostType.Materials, name: "Materials" },
];

// Beam groups by the first option until one is picked, so Cost Code leads
const groupByOptions = {
  [ItivOrderField.CostCode]: "Cost Code",
  [ItivOrderField.CostDivision]: "Cost Division",
  [ItivOrderField.LocationFeature]: "Feature",
  [ItivOrderField.LocationLevel]: "Level",
  [ItivOrderField.Options]: "Option",
  [ItivOrderField.LocationRoom]: "Room",
  [ItivOrderField.GlobalPlanTask]: "Task",
};

type GroupBy = keyof typeof groupByOptions;

function isGroupBy(value: string | undefined): value is GroupBy {
  return value !== undefined && value in groupByOptions;
}

// The `serverSideSortKey`s on `TakeoffsTable`'s headers. Some order fields fail on the server, so anything else in
// `?sortBy=` falls back to Item.
const sortByFields = [
  ItivOrderField.CostCode,
  ItivOrderField.Item,
  ItivOrderField.MaterialCode,
  ItivOrderField.Quantity,
  ItivOrderField.UnitOfMeasure,
  ItivOrderField.Location,
  ItivOrderField.Options,
  ItivOrderField.GlobalPlanTask,
];

const querySchema = searchSchema.extend({
  sortBy: z.coerce.string().default(ItivOrderField.Item),
  sortDirection: z.coerce.string().default(Order.Asc),
});

function toOrder(direction: Direction): Order {
  return direction === DESC ? Order.Desc : Order.Asc;
}

type Facets = {
  elevations: UseTakeoffsLayoutState_OptionFragment[];
  options: NestedOption<OptionNode>[];
  costCodes: NamedFragment[];
  locations: LocationNode[];
};

function toFacets(planPackage: UseTakeoffsLayoutStateQuery["planPackage"]): Facets {
  const [elevations, options] = planPackage.options.partition((rpo) => rpo.type.isElevation);
  return {
    elevations: elevations.sortBy((rpo) => rpo.displayName),
    options: toOptionTree(options),
    costCodes: planPackage.costCodes.sortBy((cc) => cc.name.toLowerCase()),
    locations: toLocationTree(planPackage.itemLocations),
  };
}

function createFilterDefs(facets: Facets | undefined, planName: string): FilterDefs<TakeoffsFilter> {
  return {
    elevations: multiFilter({
      label: "Elevations",
      options: facets?.elevations ?? [],
      getOptionValue: (rpo) => rpo.id,
      getOptionLabel: (rpo) => rpo.displayName,
      nothingSelectedText: "None",
    }),
    options: treeFilter({
      label: "Options",
      // A code only groups its instances, so the instances are what get filtered on
      filterBy: "leaf",
      options: facets?.options ?? [],
      getOptionValue: (option) => option.id,
      // Beam uses one label for the chips and the type-ahead, so this is the full name rather than the design's
      // code-only chips, until Beam's new multi-select (DS-375, DS-396) brings its filters closer to the design
      getOptionLabel: (option) => option.label,
      getOptionMenuLabel: (option) => <OptionMenuLabel option={option} />,
      nothingSelectedText: "None",
    }),
    costCode: multiFilter({
      label: "Cost Codes",
      options: facets?.costCodes ?? [],
      getOptionValue: (cc) => cc.id,
      getOptionLabel: (cc) => cc.name,
      nothingSelectedText: "None",
    }),
    itemType: multiFilter({
      label: "Item Type",
      options: itemTypeOptions,
      getOptionValue: (type) => type.id,
      getOptionLabel: (type) => type.name,
      nothingSelectedText: "None",
    }),
    locationInPath: treeFilter({
      label: "Location",
      // The highest selected location, b/c lines sit on parent locations too, i.e. a room's own lines
      filterBy: "root",
      options: facets?.locations ?? [],
      getOptionValue: (location) => location.id,
      getOptionLabel: (location) => location.name,
      nothingSelectedText: "None",
    }),
    basePlan: checkboxFilter({ label: `Base Plan (${planName})` }),
    modifiedInCurrentVersion: checkboxFilter({ label: "User Changed" }),
  };
}

/** An Options filter row: one instance of an option, or the code grouping a plan's instances of it across rooms. */
type OptionNode = {
  id: string;
  /** Used for chips and type-ahead, so a search matches the code, name and room */
  label: string;
  name: string;
  code?: string;
  instances?: number;
};

function toOptionTree(options: UseTakeoffsLayoutState_OptionFragment[]): NestedOption<OptionNode>[] {
  return options
    .groupByObject((rpo) => rpo.code)
    .sortBy(([code]) => code)
    .map(([code, [first, ...others]]) => {
      if (others.isEmpty) return { id: first.id, label: first.displayName, name: instanceName(first), code };
      const instances = [first, ...others].sortBy((rpo) => rpo.displayName);
      return {
        id: `optionCode:${code}`,
        label: `${code} - ${first.name}`,
        name: first.name,
        code,
        instances: instances.length,
        children: instances.map((rpo) => ({ id: rpo.id, label: rpo.displayName, name: instanceName(rpo) })),
      };
    });
}

function instanceName(rpo: UseTakeoffsLayoutState_OptionFragment): string {
  return [rpo.name, rpo.location?.name].compact().join(" - ");
}

type OptionMenuLabelProps = { option: OptionNode };

// TODO: Beam gap (DS-375/DS-396): drop this once Beam is bumped past both and its new multi-select renders option trees
// with the code and count
function OptionMenuLabel(props: OptionMenuLabelProps) {
  const { option } = props;
  return (
    <span css={Css.df.aic.gap1.$}>
      {option.name}
      {option.code && <span css={Css.color(Tokens.OnSurfaceMuted).$}>{option.code}</span>}
      {option.instances !== undefined && (
        <CountBadge count={option.instances} bgColor={Tokens.SurfaceSubtle} color={Tokens.OnSurface} />
      )}
    </span>
  );
}

type LocationNode = NamedFragment & { children: LocationNode[]; defaultCollapsed?: boolean };

type LocationFacet = Pick<UseTakeoffsLayoutState_LocationFragment, "id" | "name" | "type">;

/** Each line's location under its parents. Scope locations are left out; their lines match through a parent. */
function toLocationTree(locations: UseTakeoffsLayoutState_LocationFragment[]): LocationNode[] {
  const roots: LocationNode[] = [];
  locations.forEach(({ parents, ...location }) => {
    [location, ...parents]
      .reverse()
      .filter((l) => l.type.code !== LocationType.Scope)
      .reduce(insertLocation, roots);
  });
  return roots;
}

/** Finds `location` in `siblings`, or adds it in v1's order, and returns its children. */
function insertLocation(siblings: LocationNode[], location: LocationFacet): LocationNode[] {
  const existing = siblings.find((node) => node.id === location.id);
  if (existing) return existing.children;
  const { id, name, type } = location;
  // Rooms start collapsed so the tree opens on the building's levels and rooms, not every feature
  const node = { id, name, children: [], defaultCollapsed: type.code === LocationType.Room };
  const index = siblings.findIndex((sibling) => sortsAfter(sibling.name, name));
  siblings.splice(index === -1 ? siblings.length : index, 0, node);
  return node.children;
}

/** Numbered rooms go in room-number order, everything else by name. */
function sortsAfter(sibling: string, name: string): boolean {
  const [siblingName, siblingNumber] = maybeSplitRoomNumber(sibling);
  const [nodeName, nodeNumber] = maybeSplitRoomNumber(name);
  return siblingNumber && nodeNumber ? siblingNumber > nodeNumber : siblingName.localeCompare(nodeName) > 0;
}

/** `filter` less the selections its facets no longer offer, or undefined when none went stale. */
function pruneFilter(filter: TakeoffsFilter, facets: Facets): TakeoffsFilter | undefined {
  const elevations = keepOffered(
    filter.elevations,
    facets.elevations.map((rpo) => rpo.id),
  );
  const options = keepOffered(filter.options, treeIds(facets.options));
  const costCode = keepOffered(
    filter.costCode,
    facets.costCodes.map((cc) => cc.id),
  );
  const locationInPath = keepOffered(filter.locationInPath, treeIds(facets.locations));
  const unchanged =
    elevations === filter.elevations &&
    options === filter.options &&
    costCode === filter.costCode &&
    locationInPath === filter.locationInPath;
  return unchanged ? undefined : { ...filter, elevations, options, costCode, locationInPath };
}

/** `ids` as-is when they're all still offered, so the caller can tell nothing changed. */
function keepOffered(ids: string[] | undefined, offered: string[]): string[] | undefined {
  if (!ids || ids.every((id) => offered.includes(id))) return ids;
  const kept = ids.filter((id) => offered.includes(id));
  return kept.nonEmpty ? kept : undefined;
}

function treeIds<O extends { id: string }>(nodes: NestedOption<O>[]): string[] {
  return nodes.flatMap((node) => [node.id, ...treeIds(node.children ?? [])]);
}
